import { useState } from 'react'
import { FiCreditCard, FiMapPin, FiPackage, FiShoppingCart, FiTruck, FiUser } from 'react-icons/fi'

import { api } from '../../../../api/api'
import { OffCanvas } from '../../../../components/OffCanvas'
import { Toast } from '../../../../utils/Toast'
import { PaymentMethod } from '../../../../utils/PaymentMethods'
import { maskCurrency, maskPhone, TypePayment } from '../../../../utils/utils'
import * as S from './styles'

const cardBrands = {
  [TypePayment.card_credit]: ['Elo', 'Visa', 'MasterCard', 'American Express', 'Diners', 'Hipercard', 'Goodcard', 'Verdecard', 'Sorocred', 'Credishop', 'Banricocompras'],
  [TypePayment.card_debit]: ['Visa', 'Elo', 'MasterCard', 'Banricocompras'],
}

function parsePayload(payload) {
  if (!payload) return {}
  if (typeof payload === 'object') return payload

  try {
    return JSON.parse(payload)
  } catch {
    return {}
  }
}

function getAdditionals(product) {
  const additionals = product?.additinal || product?.additionals || {}
  if (Array.isArray(additionals)) return additionals

  return Object.values(additionals).flatMap((items) => Array.isArray(items) ? items : [])
}

function getProductTotal(product) {
  const additionalsTotal = getAdditionals(product).reduce((total, additional) => (
    total + (Number(additional?.pricing || 0) * Number(additional?.quantity ?? 1))
  ), 0)

  return (Number(product?.pricing || 0) + additionalsTotal) * Number(product?.quantity || 1)
}

function getDeliveryLabel(method) {
  const labels = {
    delivery: 'Entrega',
    entrega: 'Entrega',
    pickup: 'Retirada no local',
    retirada: 'Retirada no local',
  }

  return labels[String(method || '').toLowerCase()] || method || 'Não informada'
}

function getPaymentLabel(method) {
  return PaymentMethod[method] || method || 'Não informada'
}

export function AbandonedCartDetails({ customer, onClose, onCreated, canCreate = false, companyLink }) {
  const [creating, setCreating] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('')
  const [selectedCardBrand, setSelectedCardBrand] = useState('')
  const payload = parsePayload(customer?.payload)
  const infoClient = payload?.infoClient || {}
  const products = Array.isArray(payload?.products) ? payload.products : []
  const payment = payload?.payment || {}
  const deliveryDistrict = payload?.deliveryDistrict || infoClient?.deliveryDistrict || {}
  const deliveryMaps = payload?.deliveryMaps || {}
  const customerName = customer?.name || customer?.customerName || infoClient?.name || 'Cliente não informado'
  const customerPhone = customer?.phone || infoClient?.phone
  const address = infoClient?.address || infoClient?.street
  const addressLine = [address, infoClient?.number].filter(Boolean).join(', ')
  const district = infoClient?.district || infoClient?.neighborhood || deliveryDistrict?.district
  const hasAddress = Boolean(addressLine || district || infoClient?.cep || infoClient?.complement || infoClient?.referencePoint)
  const hasMappedDelivery = Boolean(
    deliveryDistrict?.id ||
    deliveryDistrict?.idDeliveryDistrict ||
    deliveryDistrict?.district ||
    deliveryMaps?.location?.latitude ||
    deliveryMaps?.location?.longitude
  )
  const deliveryMethod = payload?.methodDelivery || infoClient?.methodDelivery || payload?.deliveryMethod || ((hasAddress || hasMappedDelivery) ? 'entrega' : '')
  const isFreeDelivery = deliveryDistrict?.free === true || Number(deliveryDistrict?.free) === 1
  const deliveryPricing = isFreeDelivery
    ? 0
    : deliveryDistrict?.pricing ?? deliveryMaps?.pricing
  const productsTotal = products.reduce((total, product) => total + getProductTotal(product), 0)
  const paymentMethod = payment?.paymentMethod || payment?.method || payload?.paymentMethod
  const moneyChange = payment?.moneyChange ?? payload?.moneyChange
  const chosenPaymentMethod = payment?.paymentMethod || selectedPaymentMethod
  const chosenCardBrand = payment?.paymentTypeCard || selectedCardBrand
  const requiresCardBrand = Boolean(cardBrands[chosenPaymentMethod])

  const missing = []
  if (!infoClient?.name?.trim()) missing.push('nome')
  if (![10, 11].includes(String(infoClient?.phone || '').replace(/\D/g, '').length)) missing.push('telefone válido')
  const explicitDelivery = infoClient?.methodDelivery || payload?.methodDelivery || payload?.deliveryMethod ||
    ((address || hasMappedDelivery) ? 'entrega' : '')
  if (!['entrega', 'retirada'].includes(explicitDelivery)) missing.push('modalidade de entrega')
  if (explicitDelivery === 'entrega') {
    if (!address?.trim()) missing.push('endereço')
    if (!String(infoClient?.number || '').trim()) missing.push('número')
    if (!String(district || '').trim()) missing.push('bairro')
  }
  if (!products.length || products.some((product) => !product?.idProduct || !product?.nameProduct ||
    !Number.isFinite(Number(product?.pricing)) || !Number.isInteger(Number(product?.quantity)) || Number(product.quantity) < 1)) {
    missing.push('produtos válidos')
  }
  if (!chosenPaymentMethod) missing.push('forma de pagamento')
  if (requiresCardBrand && !chosenCardBrand) missing.push('bandeira do cartão')

  async function createOrder() {
    if (creating) return
    if (missing.length || !companyLink) {
      Toast.warning(missing.length ? `Dados pendentes: ${missing.join(', ')}.` : 'Link da empresa indisponível.')
      return
    }
    setCreating(true)
    try {
      const request = {
        ...payload,
        link: companyLink,
        payment: {
          ...payment,
          paymentMethod: chosenPaymentMethod,
          ...(requiresCardBrand ? { paymentTypeCard: chosenCardBrand } : {}),
        },
        infoClient: {
          ...infoClient,
          methodDelivery: explicitDelivery,
          address: infoClient.address || '',
          number: infoClient.number || '',
          cep: infoClient.cep || '',
          complement: infoClient.complement || '',
          referencePoint: infoClient.referencePoint || '',
          district,
        },
      }
      const { data } = await api.post('/order/client', request)
      if (!data?.id) {
        Toast.warning('Não foi possível confirmar a criação do pedido. Confira a tela inicial antes de tentar novamente.')
        return
      }
      try {
        await api.put(`/orders/${data.id}`, { status: 'aprovado' })
        Toast.success('Pedido criado em preparo.')
        onCreated?.()
      } catch {
        Toast.warning('Pedido criado, mas não foi possível movê-lo para Em preparo. Confira a tela inicial antes de tentar novamente.')
      }
    } catch {
      // O interceptor da API exibe o erro retornado pelo servidor.
    } finally {
      setCreating(false)
    }
  }

  return (
    <OffCanvas
      id="abandoned-cart-details"
      expanded
      isModal
      width="780px"
      title="Detalhes do carrinho abandonado"
      subtitle={customerPhone ? maskPhone(String(customerPhone)) : 'Telefone não informado'}
      titleIcon={<FiShoppingCart size={20} />}
      onClose={onClose}
      paddingBody="0"
    >
      <S.Content>
        <S.Section>
          <S.SectionTitle><FiUser /> Cliente</S.SectionTitle>
          <S.InfoGrid>
            <S.Info><span>Nome</span><strong>{customerName}</strong></S.Info>
            <S.Info><span>Telefone</span><strong>{customerPhone ? maskPhone(String(customerPhone)) : 'Não informado'}</strong></S.Info>
            {infoClient?.cpf && <S.Info><span>CPF</span><strong>{infoClient.cpf}</strong></S.Info>}
          </S.InfoGrid>
        </S.Section>

        <S.Section>
          <S.SectionTitle><FiMapPin /> Endereço</S.SectionTitle>
          {hasAddress ? (
            <S.AddressCard>
              <strong>{addressLine || 'Endereço não informado'}</strong>
              {district && <span>Bairro: {district}</span>}
              {infoClient?.complement && <span>Complemento: {infoClient.complement}</span>}
              {infoClient?.cep && <span>CEP: {infoClient.cep}</span>}
              {infoClient?.referencePoint && <span>Referência: {infoClient.referencePoint}</span>}
            </S.AddressCard>
          ) : (
            <S.Empty>Endereço não informado.</S.Empty>
          )}
        </S.Section>

        <S.Section>
          <S.SectionTitle><FiTruck /> Forma de entrega</S.SectionTitle>
          <S.InfoGrid>
            <S.Info><span>Modalidade</span><strong>{getDeliveryLabel(deliveryMethod)}</strong></S.Info>
            {deliveryPricing !== undefined && (
              <S.Info>
                <span>Taxa de entrega</span>
                <strong>{Number(deliveryPricing) === 0 ? 'Grátis' : maskCurrency(deliveryPricing)}</strong>
              </S.Info>
            )}
          </S.InfoGrid>
        </S.Section>

        <S.Section>
          <S.SectionTitle>
            <span><FiPackage /> Produtos</span>
            <S.Count>{products.length}</S.Count>
          </S.SectionTitle>
          {products.length > 0 ? (
            <S.ProductList>
              {products.map((product, index) => {
                const additionals = getAdditionals(product)
                return (
                  <S.Product key={product?.id || product?.idProduct || `${product?.nameProduct}-${index}`}>
                    <S.ProductHeader>
                      <S.Quantity>{product?.quantity || 1}x</S.Quantity>
                      <S.ProductName>
                        <strong>{product?.nameProduct || product?.name || 'Produto sem nome'}</strong>
                        <span>Unitário: {maskCurrency(product?.pricing || 0)}</span>
                      </S.ProductName>
                      <S.ProductPrice>{maskCurrency(getProductTotal(product))}</S.ProductPrice>
                    </S.ProductHeader>
                    {additionals.length > 0 && (
                      <S.Additionals>
                        {additionals.map((additional, additionalIndex) => (
                          <div key={additional?.id || `${additional?.name}-${additionalIndex}`}>
                            <span>+ {additional?.quantity || 1}x {additional?.name || 'Adicional'}</span>
                            <span>{maskCurrency(Number(additional?.pricing || 0) * Number(additional?.quantity || 1))}</span>
                          </div>
                        ))}
                      </S.Additionals>
                    )}
                    {(product?.observation || product?.observations) && (
                      <S.Observation><strong>Observação:</strong> {product.observation || product.observations}</S.Observation>
                    )}
                  </S.Product>
                )
              })}
              <S.Total><span>Total dos produtos</span><strong>{maskCurrency(productsTotal)}</strong></S.Total>
            </S.ProductList>
          ) : (
            <S.Empty>Nenhum produto informado.</S.Empty>
          )}
        </S.Section>

        <S.Section>
          <S.SectionTitle><FiCreditCard /> Pagamento</S.SectionTitle>
          <S.InfoGrid>
            <S.Info><span>Forma de pagamento</span><strong>{getPaymentLabel(payment?.paymentMethod || selectedPaymentMethod || paymentMethod)}</strong></S.Info>
            {moneyChange !== undefined && Number(moneyChange) > 0 && (
              <S.Info><span>Troco para</span><strong>{maskCurrency(moneyChange)}</strong></S.Info>
            )}
          </S.InfoGrid>
          {canCreate && !payment?.paymentMethod && (
            <S.PaymentField>
              <label htmlFor="abandoned-cart-payment">Selecione a forma de pagamento</label>
              <select id="abandoned-cart-payment" value={selectedPaymentMethod} onChange={(event) => {
                setSelectedPaymentMethod(event.target.value)
                setSelectedCardBrand('')
              }}>
                <option value="">Selecione</option>
                <option value={TypePayment.money}>Dinheiro</option>
                <option value={TypePayment.pix}>PIX</option>
                <option value={TypePayment.card_credit}>Cartão de crédito</option>
                <option value={TypePayment.card_debit}>Cartão de débito</option>
              </select>
            </S.PaymentField>
          )}
          {canCreate && requiresCardBrand && !payment?.paymentTypeCard && (
            <S.PaymentField>
              <label htmlFor="abandoned-cart-card-brand">Bandeira do cartão</label>
              <select id="abandoned-cart-card-brand" value={selectedCardBrand} onChange={(event) => setSelectedCardBrand(event.target.value)}>
                <option value="">Selecione</option>
                {cardBrands[chosenPaymentMethod].map((brand) => <option key={brand} value={brand}>{brand}</option>)}
              </select>
            </S.PaymentField>
          )}
        </S.Section>
        {canCreate && (
          <S.Section>
            {missing.length > 0 && <S.Empty>Para criar o pedido, informe: {missing.join(', ')}.</S.Empty>}
            <S.CreateButton type="button" onClick={createOrder} disabled={creating || !companyLink}>
              {creating ? 'Criando pedido...' : 'Criar pedido em preparo'}
            </S.CreateButton>
          </S.Section>
        )}
      </S.Content>
    </OffCanvas>
  )
}
